import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Download, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

const numToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero';

  const twoDigits = (n) => {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 === 0 ? '' : ' ' + ones[n % 10]);
  };

  const threeDigits = (n) => {
    if (n < 100) return twoDigits(n);
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 === 0 ? '' : ' ' + twoDigits(n % 100));
  };

  num = parseInt(num, 10);
  if (num < 0) return 'Negative ' + numToWords(-num);

  let result = '';

  if (num >= 10000000) {
    result += threeDigits(Math.floor(num / 10000000)) + ' Crore ';
    num %= 10000000;
  }
  if (num >= 100000) {
    result += twoDigits(Math.floor(num / 100000)) + ' Lakh ';
    num %= 100000;
  }
  if (num >= 1000) {
    result += twoDigits(Math.floor(num / 1000)) + ' Thousand ';
    num %= 1000;
  }
  if (num >= 100) {
    result += ones[Math.floor(num / 100)] + ' Hundred ';
    num %= 100;
  }
  if (num > 0) {
    result += twoDigits(num);
  }

  return result.trim();
};

const PCBPaybillSummaryView = ({
  approvedRuns = [],
  initialRunId,
  initialDept = 'ALL',
  initialMonth = 'ALL',
  initialYear = 'ALL',
}) => {
  const { hasPermission } = useAuth();
  const [runsList, setRunsList] = useState(approvedRuns);
  const [paybillData, setPaybillData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const currentDate = new Date();
  const currentMonthStr = String(currentDate.getMonth() + 1);
  const currentYearStr = String(currentDate.getFullYear());

  const [selectedDept, setSelectedDept] = useState(initialDept);
  const [fromMonth, setFromMonth] = useState(currentMonthStr);
  const [fromYear, setFromYear] = useState(currentYearStr);
  const [toMonth, setToMonth] = useState(currentMonthStr);
  const [toYear, setToYear] = useState(currentYearStr);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [paybillDataList, setPaybillDataList] = useState([]);

  const monthsList = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const yearsList = Array.from({ length: Math.max(1, new Date().getFullYear() - 2025 + 1) }, (_, i) => String(2025 + i));

  const printRef = useRef();

  useEffect(() => {
    setSelectedDept(initialDept);
  }, [initialDept]);



  const periodsInRange = useMemo(() => {
    const list = [];
    const startVal = Number(fromYear) * 12 + Number(fromMonth);
    const endVal = Number(toYear) * 12 + Number(toMonth);

    let currVal = startVal;
    while (currVal <= endVal) {
      const y = Math.floor((currVal - 1) / 12);
      const m = ((currVal - 1) % 12) + 1;

      const actualRun = runsList.find(
        (r) => Number(r.payroll_month) === m && Number(r.payroll_year) === y
      );

      if (actualRun) {
        list.push({
          id: String(actualRun.id),
          payroll_month: m,
          payroll_year: y,
          status: actualRun.status || 'approved',
          isVirtual: false
        });
      } else {
        list.push({
          id: `VIRTUAL_${m}_${y}`,
          payroll_month: m,
          payroll_year: y,
          status: 'no data',
          isVirtual: true
        });
      }

      currVal += 1;
    }
    return list;
  }, [runsList, fromMonth, fromYear, toMonth, toYear]);

  useEffect(() => {
    if (approvedRuns && approvedRuns.length > 0) {
      setRunsList(approvedRuns);
    } else {
      const fetchRuns = async () => {
        try {
          const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            setRunsList(Array.isArray(data) ? data : []);
          }
        } catch (err) {
          console.error('Failed to load fallback runs in PCBPaybillSummaryView:', err);
        }
      };
      fetchRuns();
    }
  }, [approvedRuns]);

  const loadAllPaybillData = async (periods, dept) => {
    setLoading(true);
    setError('');
    try {
      const promises = periods.map(async (period) => {
        if (period.isVirtual) {
          return {
            month_year: `${getMonthName(period.payroll_month).toUpperCase()} ${period.payroll_year}`,
            dept: dept || 'ALL',
            totals: {
              net_pay: 0,
              pt: 0,
              pfsub: 0,
              gis: 0,
              pfadv: 0,
              vpf: 0,
              f_adv: 0,
              others: 0,
              it: 0,
              lic: 0,
              bank: 0,
              soc: 0,
              l_fees: 0,
              rd: 0,
              tot_cash: 0,
              cpf: 0,
              wt: 0
            }
          };
        } else {
          const url = dept && dept !== 'ALL'
            ? `/api/v1/reports/pay-bill-data/${period.id}?sec_code=${encodeURIComponent(dept)}`
            : `/api/v1/reports/pay-bill-data/${period.id}`;
          const response = await fetch(buildApiUrl(url), {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          if (!response.ok) {
            throw new Error(`Failed to load paybill data for period ${period.payroll_month}/${period.payroll_year}`);
          }
          return await response.json();
        }
      });

      const results = await Promise.all(promises);
      setPaybillDataList(results);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error loading paybill summary data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (periodsInRange.length > 0) {
      loadAllPaybillData(periodsInRange, selectedDept);
    } else {
      setPaybillDataList([]);
    }
  }, [periodsInRange, selectedDept]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      let url = `/api/v1/reports/pay-bill-summary-pdf/0?from_month=${fromMonth}&from_year=${fromYear}&to_month=${toMonth}&to_year=${toYear}`;
      if (selectedDept && selectedDept !== 'ALL') {
        url += `&sec_code=${encodeURIComponent(selectedDept)}`;
      }
      await downloadAuthenticatedFile(
        url,
        `Paybill_Summary_${selectedDept || 'ALL'}_${fromMonth}_${fromYear}_to_${toMonth}_${toYear}.pdf`
      );
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF report: ' + (err.message || 'Server error'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getMonthName = (month) => {
    return new Date(2025, month - 1).toLocaleString('default', { month: 'long' });
  };



  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="print:hidden bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 w-full">
          <div className="flex flex-wrap items-center gap-3">
            <Calendar className="w-5 h-5 text-blue-600 self-center" />

            {/* From Period */}
            <div className="flex items-center gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">From Month</label>
                <select
                  value={fromMonth}
                  onChange={(e) => setFromMonth(e.target.value)}
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[130px]"
                >
                  {monthsList.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">From Year</label>
                <select
                  value={fromYear}
                  onChange={(e) => setFromYear(e.target.value)}
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                >
                  {yearsList.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <span className="text-gray-400 font-bold self-end mb-2">TO</span>

            {/* To Period */}
            <div className="flex items-center gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">To Month</label>
                <select
                  value={toMonth}
                  onChange={(e) => setToMonth(e.target.value)}
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[130px]"
                >
                  {monthsList.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">To Year</label>
                <select
                  value={toYear}
                  onChange={(e) => setToYear(e.target.value)}
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                >
                  {yearsList.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 self-end">
            {hasPermission('report:export') && (
              <button
                onClick={handleDownloadPdf}
                disabled={periodsInRange.length === 0 || downloadingPdf}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors ${periodsInRange.length === 0 || downloadingPdf ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {downloadingPdf ? 'Downloading...' : 'Download PDF'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Print break style */}
      <style>{`
        @media print {
          .print\\:page-break-after-always {
            page-break-after: always !important;
            break-after: always !important;
          }
        }
      `}</style>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      ) : (periodsInRange.length === 0 || paybillDataList.length === 0) ? (
        <div className="bg-white p-12 text-center rounded-xl border border-gray-200 shadow-sm text-gray-500 font-medium">
          No paybill summary data available for the selected month/year period.
        </div>
      ) : (
        <div ref={printRef} className="space-y-8 print:space-y-0">
          {paybillDataList.map((paybillData, idx) => {
            const totals = paybillData?.totals || {};
            const netPay = Math.round(Number(totals.net_pay || 0));
            const pt = Math.round(Number(totals.pt || 0));
            const pfsub = Math.round(Number(totals.pfsub || 0));
            const gis = Math.round(Number(totals.gis || 0));
            const pfadv = Math.round(Number(totals.pfadv || 0));
            const vpf = Math.round(Number(totals.vpf || 0));
            const f_adv = Math.round(Number(totals.f_adv || 0));
            const others = Math.round(Number(totals.others || 0));
            const it = Math.round(Number(totals.it || 0));
            const lic = Math.round(Number(totals.lic || 0));
            const bank = Math.round(Number(totals.bank || 0));
            const soc = Math.round(Number(totals.soc || 0));
            const l_fees = Math.round(Number(totals.l_fees || 0));
            const rd = Math.round(Number(totals.rd || 0));
            const cash = Math.round(Number(totals.tot_cash || 0));
            const cpf = Math.round(Number(totals.cpf || 0));
            const wt = Math.round(Number(totals.wt || 0));

            const summaryItems = [
              { label: 'State Bank Of India (GM)', value: netPay },
              { label: 'Profession Tax', value: pt },
              { label: 'Provident Subscription', value: pfsub },
              { label: 'Group Insurance Scheme', value: gis },
              { label: 'Provident Fund Loan', value: pfadv },
              { label: 'Voluntary PF', value: vpf },
              { label: 'Festival Advance', value: f_adv },
              { label: 'Others', value: others },
              { label: 'Income Tax', value: it },
              { label: 'LIC', value: lic },
              { label: 'Bank Total', value: bank },
              { label: 'Society', value: soc },
              { label: 'License Fee', value: l_fees },
              { label: 'Recurring Deposit', value: rd },
              { label: 'Cash', value: cash },
              { label: 'CPF', value: cpf },
              { label: 'Water Tax', value: wt },
            ];

            const grandTotal = summaryItems.reduce((acc, item) => acc + item.value, 0);

            return (
              <div key={idx} className="bg-white p-10 rounded-xl border border-gray-300 shadow-sm print:p-0 print:border-none print:shadow-none font-mono text-sm text-gray-900 leading-relaxed max-w-3xl mx-auto print:page-break-after-always print:mb-0">
                {/* Main Title Header */}
                <div className="font-bold text-base tracking-wider uppercase mb-8">
                  SUMMARY FOR {paybillData.dept} FOR THE MONTH OF {paybillData.month_year}
                </div>

                {/* List of Summary Items */}
                <div className="space-y-1.5 max-w-xl text-base">
                  {summaryItems.map((item, sIdx) => (
                    <div key={sIdx} className="flex justify-between items-center py-0.5">
                      <span>{item.label}</span>
                      <span className="font-semibold tracking-wide">₹{item.value}</span>
                    </div>
                  ))}

                  {/* Grand Total Row */}
                  <div className="flex justify-between items-center py-1 font-extrabold text-base border-t border-transparent mt-2">
                    <span>Grand Total</span>
                    <span>₹{grandTotal}</span>
                  </div>
                </div>

                {/* Sanction Payment Line */}
                <div className="mt-12 text-base font-medium">
                  Passed for the payment of ₹{Intl.NumberFormat('en-IN').format(grandTotal)}/- (Rs. {numToWords(grandTotal)} Only).
                </div>

                {/* Horizontal Line */}
                <div className="mt-4 border-b border-gray-800 w-full" />

                {/* Signatures Row 1 */}
                <div className="mt-20 flex justify-between items-center text-base font-bold">
                  <div>Prepared By</div>
                  <div>Checked By</div>
                  <div>Chief Accountant</div>
                </div>

                {/* Signatures Row 2 (CEO) */}
                <div className="mt-20 text-right text-base font-bold space-y-1">
                  <div>Chief Executive Officer</div>
                  <div>Pune Cantonment Board</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PCBPaybillSummaryView;
